import React, { useEffect, useRef, useState } from 'react';
import { useFileActions, useFileUpload } from '../../hooks';
import { bytesToSize, cn } from '@/libs';
import { UploadFile } from '@/ui-kit';
import { FileWithPreview } from '@/ui-kit/upload-file/types';

import './InputFile.scss';
import { FieldErrorMessage, useFormActions, useFormField, useFormState } from '../../core';
import { getResultErrorMessage } from '@/store/utils/resultThunkHandler';
import { config } from '../../config';
import { StringRecord } from '@/typings';
import _ from 'lodash';
import { Accept } from 'react-dropzone';
import { AuthHttpService } from '@/auth';

type GeneratedCancelTokenSource = ReturnType<
  AuthHttpService['generateCancelTokenSource']
>;

interface InputFileProps {
  label: string;
  required?: boolean;
  name: string;
  hint?: string;
  multiple?: boolean;
  placeholder?: string;
  previousFiles: StringRecord<string | undefined>;
  maxFiles?: number;
  maxSize?: number;
  accept?: Accept;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
}

export const InputFile: React.FC<InputFileProps> = ({
  label,
  required = true,
  hint,
  previousFiles,
  maxFiles,
  maxSize,
  accept,
  disabled,
  ...props
}) => {
  const { uploadFile, deleteFile, refreshSummary } = useFileActions();

  const { setFieldValue } = useFormActions();
  const { submitCount } = useFormState();

  const [, minFilesMeta] = useFormField<number>(
    `${props.name}${config.minFilesCountFieldSuffix}`,
  );

  const [uploadError, setUploadError] = useState<string | null>(null);

  const hasError = Boolean(
    uploadError ||
      (minFilesMeta.error &&
        (minFilesMeta.touched || submitCount > 0)),
  );

  const [cancelTokenList, setCancelTokenList] = useState<
    Record<string, GeneratedCancelTokenSource>
  >({});

  const previousFilesRef = useRef<StringRecord<string | undefined> | null>(
    null,
  );

  const { files, clearFiles, setFile, updateFile, removeFile, removeAll } =
    useFileUpload();

  // Stored files reach `files.uploaded` a render after mount; a 0 published before then reads as
  // their removal. Keyed on that state, not on the load effect having run: StrictMode reruns this sync first.
  const awaitingStoredFiles = useRef(Object.keys(previousFiles).length > 0);

  // The count the last real sync wrote; null until then.
  const syncedCount = useRef<number | null>(null);

  useEffect(() => {
    const uploadedCount = Object.keys(files.uploaded).length;

    if (awaitingStoredFiles.current) {
      if (uploadedCount === 0) return;

      awaitingStoredFiles.current = false;
    }

    const changed =
      syncedCount.current !== null && syncedCount.current !== uploadedCount;
    syncedCount.current = uploadedCount;

    // Keep the derived `${fieldId}_minFilesCount` Formik field in sync with the
    // live upload state. This is what FileFormField validation checks against.
    setFieldValue(
      `${props.name}${config.minFilesCountFieldSuffix}`,
      uploadedCount,
      // A group rule can read this count too (explanation or document), so a change
      // re-validates even while this field's own error is not showing.
      changed || Boolean(minFilesMeta.touched || minFilesMeta.error),
    );
  }, [files.uploaded, minFilesMeta.error, minFilesMeta.touched, props.name, setFieldValue]);

  const removeTokenFromList = (fileName: string) => {
    setCancelTokenList(prevState => {
      const newState = { ...prevState };
      delete newState[fileName];
      return newState;
    });
  };

  const labelClasses = cn('input-file__label', {
    'input-file__label--disabled': disabled,
  });

  const onDeleteUploadedFile = (fileName: string) => async () => {
    const realName = files.uploaded[fileName]?.realName;

    if (realName !== undefined) {
      const response = await deleteFile({
        itemId: props.name,
        realName,
      });

      if (response.success) {
        removeFile(fileName);
        refreshSummary();
      }
    }
  };

  const onAcceptedFiles = async (files: FileWithPreview[]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const uploadPromises: Array<any> = [];
    let anyStored = false;

    clearFiles();
    setUploadError(null);

    files.map(file => {
      const cancelToken = new AuthHttpService().generateCancelTokenSource();

      setCancelTokenList(prevState => ({
        ...prevState,
        [file.name]: cancelToken,
      }));

      setFile({
        name: file.name,
        preview: file.preview,
        loaded: bytesToSize(0),
        total: bytesToSize(file.size),
        progress: 0,
      });

      const promise = (async () => {
        const result = await uploadFile({
          itemId: props.name,
          file,
          onUploadProgress: progressEvent => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;

            updateFile({
              name: file.name,
              loaded: bytesToSize(progressEvent.loaded),
              total: bytesToSize(progressEvent.total ?? 0),
              progress,
            });
          },
          cancelToken: cancelToken.token,
        });

        if (result.success && result.data) {
          anyStored = true;
          updateFile({
            name: file.name,
            loaded: bytesToSize(0),
            total: bytesToSize(0),
            progress: 100,
            realName: result.data,
          });
        } else {
          removeTokenFromList(file.name);
          removeFile(file.name);
          setUploadError(getResultErrorMessage(result));
        }
      })();

      uploadPromises.push(promise);
    });

    try {
      await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Error uploading files', error);
    }

    // Once per batch, not per file: the summary is section-wide, and a failed batch
    // changed nothing on the server to re-read.
    if (anyStored) refreshSummary();
  };

  const onCancelUploadingFile = (fileName: string) => () => {
    const cancelToken = cancelTokenList[fileName];

    if (cancelToken) {
      cancelToken.cancel();
      removeTokenFromList(fileName);
    }

    removeFile(fileName);
  };

  const onDeleteUploadingFile = (fileName: string) => () => {
    removeFile(fileName);
  };

  const onDeleteAllUploadingFiles = () => {
    Object.keys(cancelTokenList).forEach(fileName => {
      const cancelToken = cancelTokenList[fileName];
      cancelToken.cancel();
    });
    setCancelTokenList({});
    removeAll();
  };

  useEffect(() => {
    if (
      !previousFilesRef.current ||
      (Object.keys(previousFiles).length > 0 &&
        !_.isEqual(previousFilesRef.current, previousFiles))
    ) {
      previousFilesRef.current = previousFiles;
      Object.keys(previousFiles).forEach(realName => {
        setFile({
          name: previousFiles[realName] ?? '',
          loaded: bytesToSize(0),
          total: bytesToSize(0),
          progress: 100,
          realName,
        });
      });
    }
  }, [previousFiles, setFile]);

  return (
    <div className="input-file__container">
      <div className="input-file__text-container">
        <span className={labelClasses}>
          {label}
          {required && (
            <span style={{ color: 'var(--color_error)' }}> *</span>
          )}
        </span>
        {hint && <p className="input-file__description">{hint}</p>}
      </div>
      <UploadFile
        name={props.name}
        uploadedFiles={files.uploaded}
        uploadingFiles={files.uploading}
        onDeleteUploadedFile={onDeleteUploadedFile}
        onCancelUploadingFile={onCancelUploadingFile}
        onDeleteUploadingFile={onDeleteUploadingFile}
        onDeleteAllUploadingFiles={onDeleteAllUploadingFiles}
        onAcceptedFiles={onAcceptedFiles}
        maxFiles={maxFiles}
        maxSize={maxSize}
        accept={accept}
        disabled={disabled}
        hasError={hasError}
      />
      {uploadError && <FieldErrorMessage message={uploadError} />}
    </div>
  );
};
